// ST-32 -Claude- Nearrme consumers with order history across companies.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { isNearrmeAdminRole, listCompanies, listConsumers } from '../api';
import { useAdminAuth } from '../useAdminAuth';
import { nextSort, SortHeader, sortRows, SortState } from '../tableSort';

export function AdminConsumersPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const [companyCode, setCompanyCode] = useState(isNearrmeAdmin ? '__all' : user?.companyCode ?? '');
  const [selectedConsumerId, setSelectedConsumerId] = useState<number | null>(null);
  const [sort, setSort] = useState<SortState<'consumer' | 'orders' | 'spend'>>({ key: 'consumer', direction: 'asc' });

  const companies = useQuery({ queryKey: ['admin', 'companies'], queryFn: listCompanies });
  const consumers = useQuery({
    queryKey: ['admin', 'consumers', companyCode],
    queryFn: () => listConsumers(companyCode),
    enabled: !!companyCode,
  });
  const selectedConsumer = (consumers.data ?? []).find((consumer) => consumer.consumerId === selectedConsumerId)
    ?? consumers.data?.[0];
  const sortedConsumers = sortRows(consumers.data ?? [], sort, {
    consumer: (consumer) => consumer.name,
    orders: (consumer) => consumer.orderCount,
    spend: (consumer) => consumer.totalSpendInr,
  });

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Consumers</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        Nearrme-owned consumer accounts. Purchases show which company and store fulfilled each order.
      </p>

      <Card padded className="mt-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Purchase scope</label>
            <select
              value={companyCode}
              onChange={(e) => {
                setCompanyCode(e.target.value);
                setSelectedConsumerId(null);
              }}
              disabled={!isNearrmeAdmin}
              className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-70"
            >
              {isNearrmeAdmin && <option value="__all">All companies</option>}
              {(companies.data ?? []).map((c) => (
                <option key={c.companyCode ?? ''} value={c.companyCode ?? ''}>
                  {c.companyCode} - {c.companyName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {(companies.isLoading || consumers.isLoading) && (
        <div className="mt-6 space-y-2">
          {[1, 2, 3].map((i) => <LoadingSkeleton key={i} className="h-12 w-full" />)}
        </div>
      )}

      {consumers.error && (
        <Card padded className="mt-6">
          <div className="text-red-600 dark:text-red-400 text-sm">{(consumers.error as Error).message}</div>
        </Card>
      )}

      {consumers.data && (
        consumers.data.length === 0 ? (
          <EmptyState title="No consumers yet" description="No Nearrme consumer activity matches this scope yet." />
        ) : (
          <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,440px)_1fr]">
            <Card padded={false} className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Consumer" sortKey="consumer" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Orders" sortKey="orders" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                    <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Spend" sortKey="spend" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {sortedConsumers.map((consumer) => (
                    <tr
                      key={consumer.consumerId}
                      onClick={() => navigate(`/admin/consumers/${consumer.consumerId}`)}
                      tabIndex={0}
                      role="link"
                      className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-900 dark:focus:bg-slate-900"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 dark:text-slate-100">{consumer.name}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{consumer.email}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{consumer.phoneNumber}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{consumer.orderCount}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
                        Rs. {consumer.totalSpendInr.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <Card padded>
              <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-50">
                {selectedConsumer?.name ?? 'Consumer details'}
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Orders show what the consumer bought, who they bought from, and whether fulfilment was online or store-based.
              </p>

              <div className="mt-4 space-y-3">
                {(selectedConsumer?.orders ?? []).length === 0 ? (
                  <EmptyState title="No orders for this consumer" />
                ) : (
                  selectedConsumer?.orders.map((order) => (
                    <div key={order.consumerOrderId} className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-slate-100">{order.productName}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {order.brandName ?? 'Brand pending'} - {order.companyCode}
                          </div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {order.storeName ?? 'Online fulfilment'} - {order.fulfilmentMode}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-slate-900 dark:text-slate-100">Rs. {order.totalInr.toLocaleString('en-IN')}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{new Date(order.createdAtUtc).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        )
      )}
    </div>
  );
}
